/**
 * Generic repository over sealed envelopes.
 *
 * Every mutation writes the record and its audit entry in one Dexie
 * transaction, so a change can never exist without its provenance. Sealing and
 * opening an envelope is awaited through `Dexie.waitFor`, which keeps the
 * transaction alive across a non-Dexie promise — without it the transaction
 * would commit early and the atomicity would be a comfortable illusion.
 *
 * Only the crypto goes through `waitFor`. A `waitFor` parks the transaction
 * until its promise settles, so anything wrapped in one must not itself touch
 * a table.
 */

import Dexie, { type Table } from 'dexie';
import { openJson, sealJson } from '@/crypto/primitives';
import type { AuditEntityType } from '@/domain/types';
import { appendAudit, diffRecords, type AuditContext } from './audit';
import { PAYLOAD_VERSION, STATIC_BUCKET, db, type Envelope } from './schema';

/** Any Dexie table, regardless of its row and key types. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyTable = Table<any, any, any>;

export interface RepositoryOptions<T extends { id: string }, E extends Envelope> {
  /**
   * Resolved per call rather than held, because `db` is re-pointed at the
   * signed-in driver's database. A table captured when this module first loaded
   * would keep writing to whichever account happened to be open at the time.
   */
  table: () => Table<E, string>;
  entityType: AuditEntityType;
  /** Partition key. Time-series entities use their month; the rest are static. */
  bucketOf: (entity: T) => string;
  /** Clear-text index columns beyond the shared envelope fields. */
  indexOf?: (entity: T) => Omit<E, keyof Envelope>;
  /** Human-readable identification for the audit log, e.g. `Fahrt 14.07.2026`. */
  labelOf: (entity: T) => string;
}

export class SealedRepository<T extends { id: string }, E extends Envelope = Envelope> {
  constructor(private readonly options: RepositoryOptions<T, E>) {}

  private async pack(dataKey: CryptoKey, entity: T): Promise<E> {
    const sealed = await sealJson(dataKey, entity);
    const base: Envelope = {
      id: entity.id,
      bucket: this.options.bucketOf(entity),
      iv: sealed.iv,
      ct: sealed.ct,
      pv: PAYLOAD_VERSION,
    };
    return { ...base, ...(this.options.indexOf?.(entity) ?? {}) } as E;
  }

  private async unpack(dataKey: CryptoKey, envelope: E): Promise<T> {
    return openJson<T>(dataKey, { iv: envelope.iv, ct: envelope.ct });
  }

  async get(dataKey: CryptoKey, id: string): Promise<T | undefined> {
    const envelope = await this.options.table().get(id);
    return envelope ? this.unpack(dataKey, envelope) : undefined;
  }

  async getMany(dataKey: CryptoKey, ids: readonly string[]): Promise<Map<string, T>> {
    const envelopes = await this.options.table().bulkGet([...ids]);
    const result = new Map<string, T>();
    for (const envelope of envelopes) {
      if (!envelope) continue;
      result.set(envelope.id, await this.unpack(dataKey, envelope));
    }
    return result;
  }

  async all(dataKey: CryptoKey): Promise<T[]> {
    const envelopes = await this.options.table().toArray();
    return Promise.all(envelopes.map((e) => this.unpack(dataKey, e)));
  }

  async byBuckets(dataKey: CryptoKey, buckets: readonly string[]): Promise<T[]> {
    const envelopes = await this.options.table().where('bucket').anyOf([...buckets]).toArray();
    return Promise.all(envelopes.map((e) => this.unpack(dataKey, e)));
  }

  async count(): Promise<number> {
    return this.options.table().count();
  }

  async create(ctx: AuditContext, entity: T, reason?: string): Promise<T> {
    const { entityType, labelOf } = this.options;
    const table = this.options.table();
    await db_transaction([table], async () => {
      const envelope = await Dexie.waitFor(this.pack(ctx.dataKey, entity));
      await table.add(envelope);
      await appendAudit(ctx, {
        action: 'create',
        entityType,
        entityId: entity.id,
        entityLabel: labelOf(entity),
        changes: diffRecords(undefined, entity as unknown as Record<string, unknown>),
        after: entity,
        reason,
      });
    });
    return entity;
  }

  /**
   * Apply a patch. The prior state is captured verbatim into the audit entry
   * before it is replaced, so the original record survives even though the
   * table row is overwritten.
   */
  async update(
    ctx: AuditContext,
    id: string,
    patch: Partial<T>,
    options?: { reason?: string; action?: 'update' | 'lock' | 'unlock' },
  ): Promise<T> {
    const { entityType, labelOf } = this.options;
    const table = this.options.table();
    let updated!: T;

    await db_transaction([table], async () => {
      const existingEnvelope = await table.get(id);
      if (!existingEnvelope) throw new RecordNotFoundError(entityType, id);
      const before = await Dexie.waitFor(this.unpack(ctx.dataKey, existingEnvelope));
      updated = { ...before, ...patch, id: before.id };

      const envelope = await Dexie.waitFor(this.pack(ctx.dataKey, updated));
      await table.put(envelope);
      await appendAudit(ctx, {
        action: options?.action ?? 'update',
        entityType,
        entityId: id,
        entityLabel: labelOf(updated),
        changes: diffRecords(
          before as unknown as Record<string, unknown>,
          updated as unknown as Record<string, unknown>,
        ),
        before,
        after: updated,
        reason: options?.reason,
      });
    });

    return updated;
  }

  /**
   * Remove a record.
   *
   * A reason is mandatory. GoBD Rz. 108 requires that a deletion remain
   * traceable, and the full prior state stays in the audit log — so this is a
   * removal from the working set, not from the archive.
   */
  async remove(ctx: AuditContext, id: string, reason: string): Promise<void> {
    const { entityType, labelOf } = this.options;
    const table = this.options.table();
    await db_transaction([table], async () => {
      const envelope = await table.get(id);
      if (!envelope) return;
      const before = await Dexie.waitFor(this.unpack(ctx.dataKey, envelope));
      await table.delete(id);
      await appendAudit(ctx, {
        action: 'delete',
        entityType,
        entityId: id,
        entityLabel: labelOf(before),
        changes: diffRecords(before as unknown as Record<string, unknown>, {}),
        before,
        reason,
      });
    });
  }

  /** Write without an audit entry. Only for restoring a verified backup. */
  async restoreRaw(dataKey: CryptoKey, entities: readonly T[]): Promise<void> {
    const envelopes = await Promise.all(entities.map((e) => this.pack(dataKey, e)));
    await this.options.table().bulkPut(envelopes);
  }
}

export class RecordNotFoundError extends Error {
  constructor(entityType: string, id: string) {
    super(`${entityType} ${id} wurde nicht gefunden.`);
    this.name = 'RecordNotFoundError';
  }
}

/**
 * Run a read-write transaction spanning the given tables plus the audit log.
 * Kept in one place so no caller can forget to include `auditLog` and end up
 * with a record whose provenance rolled back independently.
 */
async function db_transaction(
  tables: ReadonlyArray<AnyTable>,
  body: () => Promise<void>,
): Promise<void> {
  await db.transaction('rw', [...tables, db.auditLog], body);
}

export const staticBucket = () => STATIC_BUCKET;
